import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { endpointsApi } from "../api/endpoints";
import { billingApi } from "../api/billing";
import { formatBytes } from "../utils/format";

export default function EndpointDetail() {
  const { id } = useParams<{ id: string }>();
  const endpoint = useApi(() => endpointsApi.get(id!), [id]);
  const usage = useApi(() => billingApi.getEndpointUsage(id!), [id]);

  if (endpoint.loading) {
    return <LoadingSpinner message="Loading endpoint..." />;
  }

  if (endpoint.error) {
    return (
      <PageContainer title="Endpoint" description="">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            Unable to load endpoint: {endpoint.error}
          </p>
          <Link
            to="/endpoints"
            className="mt-2 inline-block text-sm text-simsy-600 hover:text-simsy-700"
          >
            Back to endpoints
          </Link>
        </div>
      </PageContainer>
    );
  }

  const ep = endpoint.data?.data;
  if (!ep) return null;

  const isActive =
    ep.status === "ENABLED" || ep.status === "ONLINE" || ep.status === "ACTIVE";

  return (
    <PageContainer
      title={ep.name || ep.iccid}
      description={ep.iccid}
      action={
        <Link
          to="/endpoints"
          className="text-sm text-simsy-600 hover:text-simsy-700"
        >
          Back to endpoints
        </Link>
      }
    >
      <div className="space-y-6">
        {/* Info grid */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-medium text-gray-900">
            Endpoint Details
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Status">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {ep.status}
              </span>
            </InfoField>
            <InfoField label="ICCID">{ep.iccid}</InfoField>
            {ep.msisdn && <InfoField label="MSISDN">{ep.msisdn}</InfoField>}
            {ep.servingNetwork && (
              <InfoField label="Serving Network">{ep.servingNetwork}</InfoField>
            )}
            {ep.ipAddress && (
              <InfoField label="IP Address">{ep.ipAddress}</InfoField>
            )}
            {ep.groupName && (
              <InfoField label="Group">{ep.groupName}</InfoField>
            )}
          </div>
          {ep.tags && ep.tags.length > 0 && (
            <div className="mt-4">
              <p className="mb-1 text-xs font-medium text-gray-500">Tags</p>
              <div className="flex flex-wrap gap-1">
                {ep.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex rounded bg-simsy-50 px-2 py-0.5 text-xs text-simsy-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Usage */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-medium text-gray-900">
            Data Usage
          </h2>
          {usage.loading ? (
            <LoadingSpinner message="Loading usage..." />
          ) : usage.error ? (
            <p className="text-sm text-amber-600">{usage.error}</p>
          ) : usage.data?.data ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(usage.data.data as Record<string, unknown>)
                .filter(([, v]) => typeof v === "number" || typeof v === "string")
                .map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                  >
                    <p className="text-xs font-medium uppercase text-gray-500">
                      {key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim()}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {typeof value === "number" &&
                      key.toLowerCase().includes("byte")
                        ? formatBytes(value)
                        : String(value)}
                    </p>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No usage data available</p>
          )}
        </div>

        {/* Send SMS */}
        <SendSmsCard endpointId={id!} />
      </div>
    </PageContainer>
  );
}

function InfoField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm text-gray-900">{children}</p>
    </div>
  );
}

function SendSmsCard({ endpointId }: { endpointId: string }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  async function handleSend() {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      await endpointsApi.sendSms(endpointId, { payloadText: message });
      setResult({ success: true, message: "SMS sent successfully" });
      setMessage("");
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : "Failed to send SMS",
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="mb-4 text-sm font-medium text-gray-900">Send SMS</h2>
      <div className="space-y-3">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter message..."
          rows={3}
          maxLength={160}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-simsy-500 focus:outline-none focus:ring-2 focus:ring-simsy-500"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {message.length}/160 characters
          </span>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="rounded-lg bg-simsy-600 px-4 py-2 text-sm font-medium text-white hover:bg-simsy-700 focus:outline-none focus:ring-2 focus:ring-simsy-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? "Sending..." : "Send SMS"}
          </button>
        </div>
        {result && (
          <p
            className={`text-sm ${result.success ? "text-green-600" : "text-red-600"}`}
          >
            {result.message}
          </p>
        )}
      </div>
    </div>
  );
}
